import { useMemo } from 'react';

import { GeneralSettingsPreview } from '@work4all/components/lib/components/entity-preview/general-settings-preview/GeneralSettingsPreview';
import { EntityPreviewContainerProps } from '@work4all/components/lib/components/entity-preview/types';

import { useDataProvider } from '@work4all/data';

import { TicketCategory1 } from '@work4all/models/lib/Classes/TicketCategory1.entity';
import { TicketKind } from '@work4all/models/lib/Classes/TicketKind.entity';
import { Topic } from '@work4all/models/lib/Classes/Topic.entity';
import { COLUMNS_BY_GENERAL_SETTINGS_TYPE } from '@work4all/models/lib/constants';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

export interface GeneralSettingsPreviewContainerProps<T>
  extends EntityPreviewContainerProps {
  entityType: Entities;
  previewFields: T;
  requestData?: DataRequest;
}

function GeneralSettingsPreviewContainer<
  T extends { id?: number | string | null; name?: string }
>(props: GeneralSettingsPreviewContainerProps<T>) {
  const {
    ids,
    onCloseClick,
    onEditClicked,
    onShareClicked,
    convertProps,
    onVisibilityToggle,
    entityType,
    previewFields,
  } = props;

  const request = useMemo(() => {
    return {
      entity: entityType,
      completeDataResponse: true,
      data: previewFields,
      ...props.requestData,
    };
  }, [entityType, previewFields, props.requestData]);

  const data = useDataProvider<T>(request);
  const entries = (data.data || []).filter((x) => {
    if (x.id == null) return false;
    const numericId = typeof x.id === 'string' ? parseInt(x.id, 10) : x.id;
    return !isNaN(numericId) && ids.includes(numericId);
  });

  if (!entries) {
    return null;
  }

  return (
    <GeneralSettingsPreview<T>
      entityType={entityType}
      onCloseClick={onCloseClick}
      onEditClicked={onEditClicked}
      entries={entries}
      onShareClicked={onShareClicked}
      convertProps={convertProps}
      onVisibilityToggle={onVisibilityToggle}
    />
  );
}
const previewFields = {
  id: null,
  name: null,
};

// Extended preview fields for TicketKind with additional columns
const ticketKindPreviewFields: TicketKind = {
  id: null,
  name: null,
  ...Object.fromEntries(
    (COLUMNS_BY_GENERAL_SETTINGS_TYPE[Entities.ticketKind] || []).map(
      (column) => [column, null]
    )
  ),
};

// Wrapper for TicketCategory1 using the generic GeneralSettingsPreviewContainer
export const TicketCategory1PreviewContainer: React.ComponentType<
  EntityPreviewContainerProps
> = (props) => {
  return (
    <GeneralSettingsPreviewContainer<TicketCategory1>
      {...props}
      entityType={Entities.ticketCategory1}
      previewFields={previewFields}
    />
  );
};

// Wrapper for TicketKind using the generic GeneralSettingsPreviewContainer
export const TicketKindPreviewContainer: React.ComponentType<
  EntityPreviewContainerProps
> = (props) => {
  return (
    <GeneralSettingsPreviewContainer<TicketKind>
      {...props}
      entityType={Entities.ticketKind}
      previewFields={ticketKindPreviewFields}
    />
  );
};

// Wrapper for Topic using the generic GeneralSettingsPreviewContainer
export const TopicPreviewContainer: React.ComponentType<
  EntityPreviewContainerProps
> = (props) => {
  const topicRequestData: DataRequest = useMemo(() => {
    return {
      entity: Entities.topic,
      data: previewFields,
      completeDataResponse: false,
      filter: [{ id: { $in: props.ids } }],
    };
  }, [props.ids]);
  return (
    <GeneralSettingsPreviewContainer<Topic>
      {...props}
      entityType={Entities.topic}
      previewFields={previewFields}
      requestData={topicRequestData}
    />
  );
};
